import { IDropdownOption, Separator, IRawStyle, Icon } from '@fluentui/react';
import React, { useEffect } from 'react'
import { isMobile } from 'react-device-detect';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActionType, IFormAssemblyRule, TabActionType } from '../../../../../interfaces/IFormAssembly';
import { ILayout, emptyBackgroundColor } from '../../../CustomTemplate';
import { InputType } from "../../../Validations";
import { RHFDatePickerControl } from '../DatePicker/RHFDatePickerControl';
import GridControl from '../Grid/GridControl';
import { RHFCheckBoxControl } from '../RHFChekBoxControl';
import { RHFCurrencyControl } from '../RHFCurrencyControl';
import { RHFIntegrationControl } from '../RHFIntegrationControl';
import { RHFMaskControl } from '../RHFMaskControl';
import { RHFNumberControl } from '../RHFNumberControl';
import { RHFRichTextControl } from '../RHFRichTextControl';
import { RHFSelectMultipleControl } from '../RHFSelectMultipleControl';
import { RHFSelectSingleControl } from '../RHFSelectSingleControl';
import { RHFTextBoxAreaControl } from '../RHFTextBoxAreaControl';
import { RHFTextBoxControl } from '../RHFTextBoxControl';
import { RHFUploadRunTimeControl } from '../RHFUploadRunTimeControl';
import {
    getFontStyle,
    getFontWeight,
    getLayoutBackgroundColor,
    getLayoutFontColor,
    getLayoutFontSize,
    getLayoutJustifyContent,
    getTextDecoration,
    onRenderLabel
} from './functions';
import {IOutputMap} from "../../../../../interfaces/IOutbound";

type UtilsProps = {
    getLabel: any
    getDefaultValue: any
    imageValues: any
    imageValuesDefault: any
    setImageValues: any
    integrationDropDownLists: { [key: string]: IDropdownOption[]; }
    customDropDownLists: { [key: string]: IDropdownOption[]; }
    templateId: string
    integrationReload: any
    getCustomFormContentControls: any
    username: string
    setErrorMessage: any
    displayIntegration: any
    getValidations: any
    contentLibrary: any
    locale: any

    //getDefaultValue:(layout: ILayout, form: UseFormReturn<any, any> | undefined, defaultValue?: string)=>void
}
type ControlComponent = {
    layout: ILayout
    form: UseFormReturn<any, any>
    utils: UtilsProps
    labelVisible: boolean
    formRules?: IFormAssemblyRule[]
    readOnly?: boolean
    isRequired?: boolean
    OnBlurOrOnChange?: any
    updateBeforeIntegration?: (data: IOutputMap[]) => void;
    isDisabled?: boolean
    fieldValue?: any
    defaultValue?: string | undefined
}


export default function CustomInput({
                                        layout,
                                        isRequired,
                                        form,
                                        utils,
                                        labelVisible,
                                        readOnly,
                                        isDisabled,
                                        defaultValue,
                                        fieldValue,
                                        OnBlurOrOnChange,
                                        updateBeforeIntegration
                                    }: ControlComponent) {
    const { t } = useTranslation(["common", "wizard", "preview"])

    return (
        <>
            {/* title on the whole field wrapper so the tooltip shows when hovering ANY
                part of the field (label, box, input) and for EVERY control type — some
                controls (e.g. dropdowns, date pickers) never put the title on a hoverable
                element, so tooltips were inconsistent. Empty tooltip -> undefined so no
                blank title attribute is rendered. */}
            {(layout.Validations.Hide === undefined || !layout.Validations.Hide) &&
                (() => {
                    const tooltipUrl = layout.TooltipUrl?.trim();
                    const hasLink = tooltipUrl !== undefined && tooltipUrl !== null && tooltipUrl.length > 0;
                    return (
                        // fl-has-linkicon turns this row into a flex layout ONLY when the field has a
                        // hyperlink, so the "i" sits on the input box's line (see modern-form-shell.css);
                        // fields without a link keep their normal block layout untouched.
                        <div className={`input-form${hasLink ? ' fl-has-linkicon' : ''}`}
                             title={layout.Tooltip ? layout.Tooltip : undefined}>
                            {inputs({
                                layout,
                                isRequired,
                                form,
                                utils,
                                labelVisible,
                                readOnly,
                                isDisabled,
                                defaultValue,
                                fieldValue,
                                OnBlurOrOnChange,
                                updateBeforeIntegration
                            }, t)}
                            {hasLink &&
                                <Icon
                                    className="fl-field-linkicon"
                                    iconName="Info"
                                    title={tooltipUrl}
                                    aria-label="Open link"
                                    role="button"
                                    onClick={() => { window.open(tooltipUrl, "_blank"); }}
                                />
                            }
                        </div>
                    );
                })()
            }
        </>
    )
}
    

/** True when an authored background colour is white (or unset). Such elements get
 *  NO background — a background is only painted when the author picked a real,
 *  non-white colour. Covers the empty sentinel and the common white spellings. */
const isWhiteBg = (color?: string): boolean => {
    const v = (color ?? "").trim().toLowerCase().replace(/\s+/g, "");
    return v === "" || v === "#fff" || v === "#ffff" || v === "#ffffff" || v === "#ffffffff"
        || v === "white" || v === "rgb(255,255,255)" || v === "rgba(255,255,255,1)";
};

const inputs = ({layout, form, utils, labelVisible, isRequired, readOnly, isDisabled, defaultValue, fieldValue, OnBlurOrOnChange, updateBeforeIntegration}:ControlComponent, t: any) => {
   
    const _style:IRawStyle = {
        color: getLayoutFontColor(layout.FontColor),
        backgroundColor: getLayoutBackgroundColor(layout.BackgroundColor),
        textAlign: getLayoutJustifyContent(layout.Align) as 'left' | 'right' | 'center',
        fontStyle: getFontStyle(layout.IsItalic),
        fontWeight: getFontWeight(layout.IsBold),
        textDecoration: getTextDecoration(layout.IsUnderlined),
        fontSize: getLayoutFontSize(layout.FontSize, InputType.SectionHeader) + "px",
    }
    
    switch (layout.Type) {
        case InputType.None: return <></>

        case InputType.Textbox:
            return layout.Validations.Mask.length > 0 ? (
                /*todo check mask*/
                <RHFMaskControl
                    getErrorMessage={(value: string) => {
                        form.setValue(layout.Id, value);
                    }}
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    required={isRequired}
                    readOnly={readOnly}
                    control={form.control}
                    setValue={form.setValue}
                    formState={form.watch}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    mask={layout.Validations.Mask}
                    maskFormat={{
                        Mask: /[a-zA-Z]/,
                        "9": /[0-9]/,
                        s: /[!@#$%^&*()]/,
                    }}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            ) : (
                <>
                 <RHFTextBoxControl
                        id={layout.Id}
                        label={utils.getLabel(layout.AdminOnly, layout.Label)}
                        onRenderLabel={(p) => {
                            return onRenderLabel(p, layout, labelVisible, _style);
                        }}
                        title={layout.Tooltip}
                        tabIndex={layout.TabIndex}
                        required={isRequired}
                        readOnly={readOnly}
                        placeholder={layout.Placeholder}
                        disabled={isDisabled}
                        control={form.control}
                        formState={form.watch}
                        setValue={form.setValue}
                        getValues={form.getValues}
                        trigger={form.trigger}
                        defaultValue={defaultValue}
                        maxLength={layout.Validations.MaxLength}
                        OnBlurOrOnChange={OnBlurOrOnChange}
                    />
                </>
                );

        case InputType.TextArea:
            return (
                <RHFTextBoxAreaControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    required={isRequired}
                    readOnly={readOnly}
                    control={form.control}
                    setValue={form.setValue}
                    placeholder={layout.Placeholder}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    maxLength={layout.Validations.MaxLength}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )

        case InputType.DropDownList:
            return (
                <RHFSelectSingleControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={() => {
                        return onRenderLabel(
                            {
                                required: isRequired,
                                id: layout.Id,
                                label: layout.Label,
                            },
                            layout,
                            labelVisible,
                            _style
                        );
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    options={
                        layout.Integration !== undefined && layout.Integration !== null ?
                            utils.integrationDropDownLists[layout.Integration.Id.toString()]
                            : +layout.ListId > 0
                                ? utils.customDropDownLists[layout.ListId]
                                : layout.ItemList.split(",").map<IDropdownOption>(
                                    (item) => {
                                        return {
                                            key: item?.trim(),
                                            text: item?.trim(),
                                        };
                                    }
                                )
                    }
                    required={isRequired}
                    readOnly={readOnly}
                    placeholder={layout.Placeholder}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    defaultValue={utils.getDefaultValue(layout, undefined, form.getValues(layout.Id))}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )

        case InputType.CheckBoxList:
            return (
                <RHFSelectMultipleControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={() => {
                        return onRenderLabel(
                            {
                                required: isRequired,
                                id: layout.Id,
                                label: layout.Label,
                            },
                            layout,
                            labelVisible,
                            _style
                        );
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    options={
                        layout.Integration !== undefined && layout.Integration !== null ?
                            utils.integrationDropDownLists[layout.Integration.Id.toString()]
                            : +layout.ListId > 0
                                ? utils.customDropDownLists[layout.ListId]
                                : layout.ItemList.split(",").map<IDropdownOption>(
                                    (item) => {
                                        return {
                                            key: item?.trim(),
                                            text: item?.trim(),
                                        };
                                    }
                                )
                    }
                    required={isRequired}
                    readOnly={readOnly}
                    placeholder={layout.Placeholder}
                    fieldValue={fieldValue}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )
        case InputType.RadioButtonList: return <></>

        case InputType.DatePicker:
            return (
                <RHFDatePickerControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    isLabelVisible={labelVisible}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    isRequired={isRequired}
                    placeholder={layout.Placeholder}
                    readOnly={readOnly}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    locale={utils.locale.current}
                    formatValue={layout.Validations.Regex}
                    formatDate={(date) => {
                        return "";
                    }}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )
        case InputType.Number:
            return (
                <RHFNumberControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    required={isRequired}
                    readOnly={readOnly}
                    placeholder={layout.Placeholder}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    maxLength={layout.Validations.MaxLength}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )

        case InputType.Currency:
            return (
                <RHFCurrencyControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    currency={layout.Validations.Currency}
                    culture={"en-US"}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    required={isRequired}
                    readOnly={readOnly}
                    placeholder={layout.Placeholder}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    maxLength={layout.Validations.MaxLength}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )
        case InputType.LineBreak:
            return (
                <Separator
                    styles={{
                        content: {
                            color: getLayoutFontColor(layout.FontColor),
                            fontWeight: getFontWeight(layout.IsBold),
                            fontStyle: getFontStyle(layout.IsItalic),
                            textDecoration: getTextDecoration(layout.IsUnderlined),
                        },
                        root: [
                            {
                                selectors: {
                                    "::before": {
                                        background: getLayoutFontColor(layout.FontColor) + "!important",
                                    },
                                },
                            },
                        ],
                    }}
                >
                    {utils.getDefaultValue(layout, form)}
                </Separator>
            )

        case InputType.SectionHeader: {
            const align = getLayoutJustifyContent(layout.Align);
            // A section heading is a plain (non-bold) SectionHeader: those are the
            // one-row titles ("1. Company Details", "A1 Governance", ...). Bold
            // SectionHeaders are the multi-row boxed instruction panels and keep
            // their block flow so they stay level with the fields beside them.
            const isHeadingRow = !layout.IsBold;
            const ROW_UNIT = "35px"; // one grid row (grid-auto-rows minmax(35px, ...))
            // Standardize section-title sizes to a 2-level scale: page title 24px,
            // major section header 22px, sub-section header 18px. Only left-column,
            // non-bold titles are re-scaled; right-column instruction boxes and the
            // bold CTA keep their authored size. The authored size is the level cue
            // (page title 28, major 23, sub 18).
            const authoredHeaderSize = getLayoutFontSize(layout.FontSize, InputType.SectionHeader);
            const isSectionTitle = (layout.Layout?.x ?? 0) === 0 && !layout.IsBold;
            const headerFontSize = isSectionTitle
                ? (authoredHeaderSize >= 24 ? 24 : authoredHeaderSize >= 19 ? 22 : 18)
                : authoredHeaderSize;
            // A highlighted header (author set a background colour, e.g. the coloured
            // section bars) gets rounded corners + a little inset so the text isn't
            // flush to the rounded edge. Plain headers keep their flush 0 padding.
            const headerBg = getLayoutBackgroundColor(layout.BackgroundColor);
            // A background is only painted when the author picked a real, non-white colour.
            const headerHasBg = !isWhiteBg(layout.BackgroundColor);
            return (
                <div
                    style={{
                        // Headings: normalise every title to one identical row box and
                        // sit the text on a shared baseline (flex-end), so headings of
                        // different authored font sizes still occupy the same one-row slot.
                        // EXCEPTION: a highlighted heading (coloured bar) is a self-
                        // contained pill, so its text is vertically CENTERED in the bar
                        // rather than dropped to the baseline (which looked low in the bar).
                        display: isHeadingRow ? "flex" : "block",
                        alignItems: isHeadingRow ? (headerHasBg ? "center" : "flex-end") : "center",
                        justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
                        minHeight: isHeadingRow ? ROW_UNIT : undefined,
                        lineHeight: isHeadingRow ? 1 : undefined,
                        fontSize: headerFontSize + "px",
                        bottom: "0px",
                        color: getLayoutFontColor(layout.FontColor),
                        backgroundColor: headerHasBg ? headerBg : undefined,
                        borderRadius: headerHasBg ? "8px" : undefined,
                        width: isMobile ? "auto" : "inherit",
                        fontWeight: getFontWeight(layout.IsBold),
                        fontStyle: getFontStyle(layout.IsItalic),
                        textAlign: align as 'left' | 'right' | 'center',
                        textDecoration: getTextDecoration(layout.IsUnderlined),
                        // Align the text edge with the field labels below/beside it. Section
                        // headers and Paragraphs both sit flush at the grid-cell edge so the
                        // two authorable text types line up consistently across the form.
                        // Highlighted (coloured) headers get a small inset instead.
                        paddingLeft: headerHasBg ? "12px" : "0px",
                        paddingRight: headerHasBg ? "12px" : "0px",
                    }}
                >
                    {layout.Label}
                </div>
            )
        }

        case InputType.Paragraph: {
            // A Paragraph placed in a right-hand column (x > 0) is an instruction that
            // sits beside a question (e.g. the "…ISO 27001 Certificate…" note next to
            // "(a) Is your organisation ISO 27001 certified?"). Give it the same grey
            // tinted box as the bold instruction panels; left-column paragraphs
            // (question labels, section intros) stay untinted. Styled in
            // modern-form-shell.css via .fl-side-instruction.
            const isSideInstruction = (layout.Layout?.x ?? 0) > 0;
            // Standardize body/intro paragraphs (left column) to 14px. Right-column
            // instruction boxes keep their authored size so they stay consistent with
            // the sibling instruction panels beside the other questions.
            const authoredParaSize = getLayoutFontSize(layout.FontSize, InputType.Paragraph);
            // Respect the designer's Paragraph size setting — Small=14 / Medium=18 /
            // Large=22 (px). Anything above the Large preset falls back to 14 for
            // legacy/imported data. (Previously non-instruction paragraphs were forced
            // to 14, ignoring Medium/Large.)
            const paraFontSize = authoredParaSize > 22 ? 14 : authoredParaSize;
            // A paragraph shows a background box ONLY when the author picked a real,
            // non-white colour (then it gets rounded corners + inset, like the coloured
            // SectionHeader bars). White / unset renders as plain text with no fill.
            // NOTE: this replaces the old automatic grey box for right-column instruction
            // paragraphs — they now need an authored colour to have a background.
            const paraBg = getLayoutBackgroundColor(layout.BackgroundColor);
            const paraColored = !isWhiteBg(layout.BackgroundColor);
            return isMobile ? (
                <span
                    title={layout.Tooltip}
                    className={isSideInstruction ? "fl-side-instruction" : undefined}
                    style={{
                        display: 'flex',
                        minHeight: "100px",
                        minWidth: "140px",
                        overflow: "hidden",
                        // Match desktop: coloured box and plain text share a 10px left inset
                        // so their text aligns vertically.
                        padding: paraColored ? "8px 10px" : "0px 10px",
                        borderRadius: paraColored ? "8px" : undefined,
                        lineHeight: 1,
                        fontSize: paraFontSize + "px",
                        color: getLayoutFontColor(layout.FontColor),
                        fontWeight: getFontWeight(layout.IsBold),
                        fontStyle: getFontStyle(layout.IsItalic),
                        // Honour the author's alignment on Paragraphs, same as SectionHeader.
                        textAlign: getLayoutJustifyContent(layout.Align) as 'left' | 'right' | 'center',
                        textDecoration: getTextDecoration(layout.IsUnderlined),
                        backgroundColor: paraColored ? paraBg : undefined
                    }}
                >
                    {layout.TextParagraph}
                </span>
            ) : (
                    <p
                        className={isSideInstruction ? "fl-side-instruction" : undefined}
                        style={{
                            // A paragraph with a real (non-white) authored colour renders as a
                            // rounded, inset coloured box; otherwise it's plain flush text with
                            // no fill. The box is set INLINE so it doesn't depend on CSS
                            // hot-reloading. (A white/unset colour paints no background.)
                            overflow: "auto",
                            overflowX: "hidden",
                            // Left inset kept the same (10px) for coloured boxes and plain
                            // paragraphs so their text lines up vertically: the coloured box
                            // text moves in from 16px, the plain text moves out from 0px.
                            padding: paraColored ? "10px 10px" : "3px 10px",
                            margin: "0",
                            lineHeight: paraColored ? 1.5 : 1,
                            borderRadius: paraColored ? "12px" : undefined,
                            boxSizing: paraColored ? ("border-box" as const) : undefined,
                            fontSize: paraFontSize + "px",
                            color: getLayoutFontColor(layout.FontColor),
                            fontWeight: getFontWeight(layout.IsBold),
                            fontStyle: getFontStyle(layout.IsItalic),
                            // Honour the author's alignment on Paragraphs, same as SectionHeader.
                            textAlign: getLayoutJustifyContent(layout.Align) as 'left' | 'right' | 'center',
                            textDecoration: getTextDecoration(layout.IsUnderlined),
                            backgroundColor: paraColored ? paraBg : undefined
                        }}
                    >
                        {layout.TextParagraph}
                    </p>
                )
        }
        case InputType.RichText:
            return (
                <RHFRichTextControl
                    id={layout.Id}
                    label={layout.Label}
                    setRteValue={undefined}
                    rteValue={undefined}
                    placeholder={layout.Placeholder}
                    onRenderLabel={() => {
                        return onRenderLabel(
                            {
                                required: isRequired,
                                id: layout.Id,
                                label: layout.Label
                            },
                            layout,
                            labelVisible,
                            _style
                        );
                    }}
                    contentLibrary={utils.contentLibrary}
                    control={form.control}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    readOnly={readOnly}
                    validations={layout.Validations}
                    tabIndex={layout.TabIndex}
                    richValue={fieldValue}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )

        case InputType.CheckBox:
            return (
                <RHFCheckBoxControl
                    id={layout.Id}
                    label={utils.getLabel(layout.AdminOnly, layout.Label)}
                    onRenderLabel={(p: any) => {
                        return onRenderLabel(p, layout, labelVisible, _style);
                    }}
                    readOnly={readOnly}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    value={form.getValues(layout.Id)}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    OnBlurOrOnChange={OnBlurOrOnChange}
                />
            )

        case InputType.GridList:
            return <GridControl layout={layout} form={form} utils={utils}
                labelVisible={labelVisible}
            />
        case InputType.Attachment: return <></>
        case InputType.Image:
            return (
                <RHFUploadRunTimeControl
                    id={layout.Id}
                    label={layout.Label}
                    onRenderLabel={() => {
                        return onRenderLabel(
                            {
                                required: isRequired,
                                id: layout.Id,
                                label: layout.Label,
                            },
                            layout,
                            labelVisible,
                            _style
                        );
                    }}
                    title={layout.Tooltip}
                    tabIndex={layout.TabIndex}
                    control={form.control}
                    setValue={form.setValue}
                    readOnly={readOnly}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    extraText={layout.TextParagraph}
                    imageValues={utils.imageValues}
                    imageValuesDefault={utils.imageValuesDefault}
                    setImageValues={utils.setImageValues}
                    rules={{
                        validate: (image: any) => {
                            if (typeof image === "string") {
                                return true;
                            }

                            if (image === null && image.size <= 0) {
                                return t("messages.errorImageSize", {
                                    ns: "wizard",
                                }).toString()
                            }

                            var filevalid = false;
                            var sizevalid = 300;
                            var extensions = ["bmp", "tiff", "jpg", "png", "jpeg", "gif", "svg+xml"];
                            var typefile = image?.type;
                            var type = typefile.split("/")[1];
                            filevalid = extensions.includes(type);
                            if (!filevalid) {
                                return t("messages.errorImageTypes", {
                                    ns: "wizard",
                                }).toString()
                            }

                            var fileSize = image.size;
                            var Sizekb = fileSize / 1024;
                            let IsValidSize = Sizekb <= sizevalid && fileSize > 0;
                            if (!IsValidSize) {
                                return t("messages.errorImageMaxSize", {
                                    ns: "wizard",
                                }).toString()
                            }

                            if (filevalid && IsValidSize) {
                                return true;
                            }
                        },
                    }}
                />
            )

        case InputType.HelpLink:
            return <></>;

        case InputType.Integration:
            return (
                <RHFIntegrationControl
                    id={layout.Id}
                    label={layout.Label}
                    inputs={layout.Children}
                    control={form.control}
                    setValue={form.setValue}
                    getValues={form.getValues}
                    trigger={form.trigger}
                    readOnly={readOnly}
                    tabIndex={layout.TabIndex}
                    defaultValue={utils.getDefaultValue(layout, form)}
                    display={utils.displayIntegration(layout)}
                    getJSXElement={(type: string, id: string, value: string, rowIndex: string, form: UseFormReturn<any, any>, labelVisible: boolean): JSX.Element => {
                        console.log('integrations', id, 'value', value)
                        let currentChildren = layout.Children.find((item) => item.Id === id);
                        if (!currentChildren) return <>{value}</>
                        const newItem = { ...currentChildren, Validations: { ...currentChildren?.Validations, DefaultValue: value === undefined ? "" : value } }
                        //currentChildren!.Validations.DefaultValue = value === undefined ? "" : value;
                        //const originalChildrenId = currentChildren!.Id;
                        //currentChildren!.Id = rowIndex.length > 0 ? `${currentChildren!.Id}-${rowIndex}` : originalChildrenId;

                        utils.getValidations(newItem, form);
                        let tempElement = <FormProvider {...form} >
                            <CustomInput layout={newItem} readOnly={newItem.Validations.ReadOnly} form={form} utils={utils} labelVisible={true} />
                        </FormProvider>
                        form.setValue(newItem.Id, newItem.Validations.DefaultValue)
                        //elementList[currentChildren!.Type](currentChildren!, form, labelVisible);
                        //currentChildren!.Id = originalChildrenId;
                        return tempElement;
                    }}
                    templateId={utils.templateId}
                    integration={layout.Integration}
                    updateBeforeIntegration={updateBeforeIntegration}
                    integrationReload={utils.integrationReload}
                    getCustomFormContentControls={utils.getCustomFormContentControls}
                    username={utils.username}
                    setCustomFormErrorMessage={utils.setErrorMessage}
                    isPickingList={layout.IsPickingList ?? false}
                    pickingList={layout.PickingList}
                    version={layout.Version}
                    backgroundcolor={layout?.BackgroundColor ?? ""}
                    fontcolor={layout?.FontColor ?? ""}
                />
            )

    }
}